import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import useCompanyCustDep from '../../../hooks/useCompanyCustDep';
import useFileUpload from '../../../hooks/useFileUpload';
import dayjs from 'dayjs';
import Commons from '../../../Services/Common/Common';
import Global from '../../../Services/Common/Global';
import HEL_BASIC from './HEL_BASIC';
import {
  useDelete1,
  useSave1,
  useSave2,
  useSave3,
  useSearch1,
  useSearch2,
  useSearch3,
} from './HEL_BASIC_QUERY';
import { BuildCategoryStats } from './HEL_BASIC_ColumnDefs';

const CreateInitialForm = () => ({
  hdBoardNo: 0,
  categoryCd: 'QNA',
  screenNm: '',
  title: '',
  content: '',
  statusCd: '10',
  fileName: '',
  saveName: '',
  filePath: '',
  createNm: '',
  createDate: '',
});

function HEL_BASICF() {
  const Dispatch = useDispatch();
  const GridRef1 = useRef();
  const { userInfo, companyInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((state) => state.SearchInfo);
  const IsAdmin = userInfo?.authCd === 'ADMIN';

  const [RowData1, SetRowData1] = useState([]);
  const [RowData2, SetRowData2] = useState([]);
  const [SelectedRowData1, SetSelectedRowData1] = useState([]);
  const [ChangedRowData1, SetChangedRowData1] = useState([]);
  const [Form, SetForm] = useState(CreateInitialForm());
  const [Filters, SetFilters] = useState({
    categoryCd: '',
    statusCd: '',
    searchText: '',
  });
  const [ReplyText, SetReplyText] = useState('');
  const [AttachFiles, SetAttachFiles] = useState([]);
  const [IsWritePage, SetIsWritePage] = useState(false);
  const [IsEditMode, SetIsEditMode] = useState(false);
  const [IsDetailOwner, SetIsDetailOwner] = useState(false);
  const [DeleteConfirmOpen, SetDeleteConfirmOpen] = useState(false);
  const [DeleteConfirmMessage, SetDeleteConfirmMessage] = useState('');
  const [DeleteConfirmMode, SetDeleteConfirmMode] = useState('list');
  const [PendingDeleteRows, SetPendingDeleteRows] = useState([]);
  const [PendingSkipCount, SetPendingSkipCount] = useState(0);
  const [AttachPreviewOpen, SetAttachPreviewOpen] = useState(false);
  const [AttachPreviewItem, SetAttachPreviewItem] = useState(null);
  const [StatsOpen, SetStatsOpen] = useState(false);
  const [CategoryStats, SetCategoryStats] = useState([]);

  const CreateWriteForm = () => ({
    ...CreateInitialForm(),
    createNm: userInfo?.userNm || userInfo?.userid || '',
    createDate: dayjs().format('YYYY-MM-DD'),
  });

  const { handleCoChange, handleCustChange } = useCompanyCustDep({
    requireCompany: false,
    requireCust: false,
    autoFetchDep: false,
    autoFetchDepDefaultAll: false,
    defaultCoAll: IsAdmin,
  });

  const GetSearchContext = () => ({
    coCd: IsAdmin ? searchInfo?.coCd || '' : userInfo?.coCd || '',
    custCd: IsAdmin
      ? searchInfo?.custCd ?? ''
      : userInfo?.custCd || searchInfo?.custCd || '',
  });

  const IsSearchReady = IsAdmin
    ? Boolean(companyInfo?.length && searchInfo?.coList?.length)
    : Boolean(userInfo?.coCd);

  const { runAsync: Search1Async } = useSearch1({});
  const { runAsync: Search2Async } = useSearch2({});
  const { runAsync: Search3Async } = useSearch3({});
  const { runAsync: Save1Async } = useSave1({});
  const { runAsync: Save2Async } = useSave2({});
  const { runAsync: Save3Async } = useSave3({});
  const { runAsync: Delete1Async } = useDelete1({});
  const { SaveLogicPreview, SaveLogicFile, DeleteLogicFile } = useFileUpload({
    oldFilePathField: 'filePath',
    newFileNameField: 'saveName',
    getTargetPath: (_item, custCd) => {
      const coCd =
        userInfo?.authCd === 'ADMIN'
          ? searchInfo?.coCd || userInfo?.coCd || ''
          : userInfo?.coCd || '';
      return `HelpDesk/${coCd}/${custCd || userInfo?.custCd || ''}/HEL_BASIC`;
    },
  });

  const ExtractPathList = (value) =>
    String(value || '')
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean);

  const NormalizePath = (raw) => {
    if (!raw || typeof raw !== 'string') return '';
    const s = raw.replace(/\\/g, '/');
    const idx = s.indexOf('PreviewFiles/');
    if (idx !== -1) return s.substring(idx);
    const marker = '/files/';
    const urlIdx = s.indexOf(marker);
    if (urlIdx !== -1) {
      const tail = s.substring(urlIdx + marker.length);
      try {
        return decodeURIComponent(tail);
      } catch {
        return tail;
      }
    }
    return s;
  };

  const ToPreviewUrl = (path) =>
    path ? `${Global.file.useUrl}files/${Commons.EncodeUrl(path)}` : '';

  const CanPreviewAttach = (fileName) => {
    const ext = String(fileName || '').split('.').pop()?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'pdf', 'hwp', 'hwpx', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'txt', 'log'].includes(
      ext
    );
  };

  const GetAttachPreviewUrl = (item) => {
    if (!item) return '';
    if (item.previewUrl) return item.previewUrl;
    const path = item.filePath || item.previewFilePath;
    return path ? ToPreviewUrl(path) : '';
  };

  const PreviewAttachLogic1 = (item) => {
    if (!CanPreviewAttach(item?.fileName)) {
      Dispatch(showSnackbar({ message: '미리보기를 지원하지 않는 형식입니다.', type: 'warning' }));
      return;
    }
    const previewUrl = GetAttachPreviewUrl(item);
    if (!previewUrl) {
      Dispatch(showSnackbar({ message: '미리보기할 파일 경로가 없습니다.', type: 'warning' }));
      return;
    }
    SetAttachPreviewItem({ ...item, previewUrl });
    SetAttachPreviewOpen(true);
  };

  const CloseAttachPreviewLogic1 = () => {
    SetAttachPreviewOpen(false);
    SetAttachPreviewItem(null);
  };

  const OpenStatsLogic1 = () => {
    if (!IsAdmin) return;
    SetCategoryStats(BuildCategoryStats(RowData1));
    SetStatsOpen(true);
  };

  const CloseStatsLogic1 = () => {
    SetStatsOpen(false);
  };

  const SearchLogic1 = async (searchText = Filters.searchText) => {
    if (!IsSearchReady) {
      Dispatch(
        showSnackbar({
          message: '회사·사업장 정보를 불러온 후 조회할 수 있습니다.',
          type: 'warning',
        })
      );
      return;
    }

    const text = typeof searchText === 'string' ? searchText : Filters.searchText;
    SetFilters((prev) => ({ ...prev, searchText: text }));

    const rows = await Search1Async({
      search: {
        ...GetSearchContext(),
        categoryCd: Filters.categoryCd || '',
        statusCd: Filters.statusCd || '',
        searchText: text || '',
      },
      userInfo,
    });
    SetRowData1(Array.isArray(rows) ? [...rows] : []);
  };

  const RefreshLogic1 = async () => {
    SetSelectedRowData1([]);
    SetChangedRowData1([]);
    GridRef1?.current?.api?.deselectAll?.();
    GridRef1?.current?.api?.setFilterModel?.(null);
    await SearchLogic1(Filters.searchText);
  };

  const SearchDetailLogic1 = async (row) => {
    const detailRows = await Search2Async({
      row: { hdBoardNo: row?.hdBoardNo || row?.HdBoardNo || 0 },
      userInfo,
    });

    if (!detailRows || detailRows.length === 0) {
      Dispatch(
        showSnackbar({
          message: '본인 글만 상세 조회할 수 있습니다.',
          type: 'warning',
        })
      );
      return false;
    }

    const detail = detailRows[0];
    SetForm({
      hdBoardNo: detail.hdBoardNo || 0,
      categoryCd: detail.categoryCd || 'QNA',
      screenNm: detail.screenNm || '',
      title: detail.title || '',
      content: detail.content || '',
      statusCd: detail.statusCd || '10',
      fileName: detail.fileName || '',
      saveName: detail.saveName || '',
      filePath: detail.filePath || '',
      createNm: detail.createNm || '',
      createDate: detail.createDate || '',
    });
    const fileNames = ExtractPathList(detail.fileName || detail.FileName);
    const saveNames = ExtractPathList(detail.saveName || detail.SaveName);
    const filePaths = ExtractPathList(detail.filePath || detail.FilePath);
    SetAttachFiles(
      filePaths.map((path, idx) => ({
        fileName: fileNames[idx] || saveNames[idx] || path.split('/').pop() || '',
        saveName: saveNames[idx] || path.split('/').pop() || '',
        filePath: path,
        previewFilePath: path,
        previewUrl: ToPreviewUrl(path),
      }))
    );

    const replies = await Search3Async({
      row: { hdBoardNo: detail.hdBoardNo || 0 },
      userInfo,
    });
    SetRowData2(replies || []);
    SetIsDetailOwner(String(detail.isOwnerYn || detail.IsOwnerYn || 'N') === 'Y');
    SetIsEditMode(false);
    return true;
  };

  const HandleRowClick1 = async (row) => {
    if (!row) return;
    const isOwnerYn = String(row?.isOwnerYn || row?.IsOwnerYn || 'N');
    const isMine = isOwnerYn === 'Y';
    if (!IsAdmin && !isMine) {
      Dispatch(
        showSnackbar({
          message: '다른 사용자가 작성한 글은 상세 조회할 수 없습니다.',
          type: 'warning',
        })
      );
      return;
    }
    SetSelectedRowData1([row]);
    SetIsDetailOwner(isMine);
    SetIsEditMode(false);
    const IsOpen = await SearchDetailLogic1(row);
    if (IsOpen) {
      SetIsWritePage(true);
    }
  };

  const AddLogic1 = () => {
    SetSelectedRowData1([]);
    SetRowData2([]);
    SetReplyText('');
    SetForm(CreateWriteForm());
    SetAttachFiles([]);
    SetIsDetailOwner(false);
    SetIsEditMode(true);
    SetIsWritePage(true);
  };

  const EditLogic1 = () => {
    if (!Form.hdBoardNo || !IsDetailOwner) return;
    SetIsEditMode(true);
  };

  const CancelEditLogic1 = async () => {
    if (!Form.hdBoardNo) return;
    await SearchDetailLogic1({ hdBoardNo: Form.hdBoardNo });
    SetIsEditMode(false);
  };

  const CloseWritePage = async () => {
    SetIsWritePage(false);
    SetIsEditMode(false);
    SetIsDetailOwner(false);
    SetReplyText('');
    SetRowData2([]);
    await SearchLogic1(Filters.searchText);
  };

  const HandleAttachInput = async (fileList) => {
    const Files = Array.from(fileList || []);
    if (Files.length === 0) return;

    const NewRows = [];
    for (const file of Files) {
      const timestamp = dayjs().format('YYYYMMDDHHmmss');
      const saveName = `${timestamp}_${file.name}`;
      const fileToSave = new File([file], saveName, {
        type: file.type || 'application/octet-stream',
      });
      const previewPathRaw = await SaveLogicPreview(fileToSave);
      const previewFilePath = NormalizePath(previewPathRaw);
      if (!previewFilePath) continue;
      NewRows.push({
        fileName: file.name,
        saveName,
        previewFilePath,
        previewUrl: ToPreviewUrl(previewFilePath),
      });
    }
    if (NewRows.length > 0) {
      SetAttachFiles((prev) => [...prev, ...NewRows]);
    }
  };

  const HandleFileDrop = async (event) => {
    event.preventDefault();
    await HandleAttachInput(event.dataTransfer?.files);
  };

  const HandleFileSelect = async (event) => {
    await HandleAttachInput(event.target.files);
    event.target.value = '';
  };

  const RemoveAttachLogic1 = async (index) => {
    const target = AttachFiles[index];
    if (!target) return;
    if (target.filePath) {
      await DeleteLogicFile(target.filePath);
    }
    SetAttachFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const DownloadAttachLogic1 = async (item) => {
    const path = item?.filePath || item?.previewFilePath;
    if (!path) return;
    const fileName = item?.fileName || path.split('/').pop() || 'downloaded_file';
    const result = await Commons.Axios(
      'fileDown',
      Global.file.useUrl + Global.urls.Execute.UrlFileDownload,
      { FileName: fileName, Url: Commons.EncodeUrl(path) }
    );
    const blob = new Blob([result.data], {
      type: result.headers['content-type'] || 'application/octet-stream',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const SaveLogic1 = async () => {
    if (!String(Form.title || '').trim()) {
      Dispatch(showSnackbar({ message: '제목을 입력하세요.', type: 'warning' }));
      return;
    }
    if (!String(Form.content || '').trim()) {
      Dispatch(showSnackbar({ message: '내용을 입력하세요.', type: 'warning' }));
      return;
    }
    if (userInfo?.authCd === 'ADMIN' && !String(searchInfo?.coCd || '').trim()) {
      Dispatch(showSnackbar({ message: '관리자는 회사를 선택하세요.', type: 'warning' }));
      return;
    }
    const CustCdForSave =
      IsAdmin
        ? searchInfo?.custCd || ''
        : userInfo?.custCd || searchInfo?.custCd || '';
    if (!CustCdForSave) {
      Dispatch(showSnackbar({ message: '사업장을 선택하세요.', type: 'warning' }));
      return;
    }

    const ExistingRows = AttachFiles.filter(
      (f) => f.filePath && !String(f.previewFilePath || '').startsWith('PreviewFiles')
    ).map((f) => ({
      fileName: f.fileName || '',
      saveName: f.saveName || f.filePath.split('/').pop() || '',
      filePath: f.filePath,
    }));

    const NewItems = AttachFiles.filter((f) =>
      String(f.previewFilePath || '').startsWith('PreviewFiles')
    ).map((f) => ({
      previewFilePath: f.previewFilePath,
      saveName: f.saveName,
      filePath: '',
    }));

    if (NewItems.length > 0) {
      await SaveLogicFile(NewItems, CustCdForSave);
    }

    const CoCdForSave =
      userInfo?.authCd === 'ADMIN'
        ? searchInfo?.coCd || userInfo?.coCd || ''
        : userInfo?.coCd || '';
    const TargetPath = `HelpDesk/${CoCdForSave}/${CustCdForSave}/HEL_BASIC`;
    const NewRows = NewItems.map((it) => ({
      fileName: it.saveName.replace(/^\d{14}_/, ''),
      saveName: it.saveName,
      filePath: `${TargetPath}/${it.saveName}`,
    }));
    const allRows = [...ExistingRows, ...NewRows];
    const fileName = allRows.map((r) => r.fileName).join('|');
    const saveName = allRows.map((r) => r.saveName).join('|');
    const filePath = allRows.map((r) => r.filePath).join('|');

    await Save1Async(
      [
        {
          ...Form,
          fileName,
          saveName,
          filePath,
        },
      ],
      {
        userInfo,
        search: { coCd: CoCdForSave, custCd: CustCdForSave },
      }
    );

    Dispatch(showSnackbar({ message: '저장되었습니다.', type: 'success' }));
    await SearchLogic1(Filters.searchText);

    if (Form.hdBoardNo) {
      await SearchDetailLogic1({ hdBoardNo: Form.hdBoardNo });
      SetIsEditMode(false);
      SetIsWritePage(true);
    } else {
      SetIsWritePage(false);
      SetReplyText('');
      SetRowData2([]);
      SetIsEditMode(false);
      SetIsDetailOwner(false);
    }
  };

  const SaveStatusLogic1 = async () => {
    if (userInfo?.authCd !== 'ADMIN') {
      Dispatch(showSnackbar({ message: '관리자만 상태 변경 가능합니다.', type: 'warning' }));
      return;
    }
    if (!Form.hdBoardNo) {
      Dispatch(showSnackbar({ message: '게시글을 선택하세요.', type: 'warning' }));
      return;
    }

    await Save2Async([{ hdBoardNo: Form.hdBoardNo, statusCd: Form.statusCd }], {
      userInfo,
    });
    Dispatch(showSnackbar({ message: '처리상태가 변경되었습니다.', type: 'success' }));
    await SearchLogic1(Filters.searchText);
    await SearchDetailLogic1({ hdBoardNo: Form.hdBoardNo });
  };

  const SaveReplyLogic1 = async () => {
    if (!Form.hdBoardNo) {
      Dispatch(showSnackbar({ message: '게시글을 먼저 선택하세요.', type: 'warning' }));
      return;
    }
    if (!String(ReplyText || '').trim()) {
      Dispatch(showSnackbar({ message: '댓글 내용을 입력하세요.', type: 'warning' }));
      return;
    }

    await Save3Async([{ hdBoardNo: Form.hdBoardNo, content: ReplyText }], { userInfo });
    SetReplyText('');
    await SearchDetailLogic1({ hdBoardNo: Form.hdBoardNo });
  };

  const CloseDeleteConfirm = () => {
    SetDeleteConfirmOpen(false);
    SetDeleteConfirmMessage('');
    SetPendingDeleteRows([]);
    SetPendingSkipCount(0);
    SetDeleteConfirmMode('list');
  };

  const OpenDeleteConfirm = (rows, message, mode = 'list', skipCount = 0) => {
    SetPendingDeleteRows(rows);
    SetDeleteConfirmMessage(message);
    SetDeleteConfirmMode(mode);
    SetPendingSkipCount(skipCount);
    SetDeleteConfirmOpen(true);
  };

  const DeleteLogic1 = () => {
    if (!Form.hdBoardNo) {
      Dispatch(showSnackbar({ message: '삭제할 게시글이 없습니다.', type: 'warning' }));
      return;
    }

    OpenDeleteConfirm([{ hdBoardNo: Form.hdBoardNo }], '삭제하시겠습니까?', 'detail');
  };

  const DeleteSelectedLogic1 = () => {
    if (!SelectedRowData1 || SelectedRowData1.length === 0) {
      Dispatch(showSnackbar({ message: '삭제할 게시글을 선택하세요.', type: 'warning' }));
      return;
    }

    const DeletableRows = IsAdmin
      ? SelectedRowData1
      : SelectedRowData1.filter(
          (row) => String(row?.isOwnerYn || row?.IsOwnerYn || 'N') === 'Y'
        );

    if (DeletableRows.length === 0) {
      Dispatch(
        showSnackbar({
          message: '본인이 작성한 글만 삭제할 수 있습니다.',
          type: 'warning',
        })
      );
      return;
    }

    const skippedCount = SelectedRowData1.length - DeletableRows.length;
    const message =
      DeletableRows.length > 1
        ? `선택한 ${DeletableRows.length}건을 삭제하시겠습니까?`
        : '삭제하시겠습니까?';

    OpenDeleteConfirm(
      DeletableRows.map((row) => ({
        hdBoardNo: row?.hdBoardNo || row?.HdBoardNo || 0,
      })),
      message,
      'list',
      skippedCount
    );
  };

  const ConfirmDeleteLogic1 = async () => {
    if (!PendingDeleteRows.length) {
      CloseDeleteConfirm();
      return;
    }

    const mode = DeleteConfirmMode;
    const skipCount = PendingSkipCount;
    const deleteCount = PendingDeleteRows.length;
    const rowsToDelete = [...PendingDeleteRows];

    CloseDeleteConfirm();

    await Delete1Async(rowsToDelete, { userInfo });

    if (mode === 'detail') {
      Dispatch(showSnackbar({ message: '삭제되었습니다.', type: 'success' }));
      AddLogic1();
      SetIsWritePage(false);
    } else {
      Dispatch(
        showSnackbar({
          message:
            skipCount > 0
              ? `${deleteCount}건 삭제되었습니다. (타인 작성 ${skipCount}건 제외)`
              : `${deleteCount}건 삭제되었습니다.`,
          type: 'success',
        })
      );
      SetSelectedRowData1([]);
    }

    await SearchLogic1(Filters.searchText);
  };

  const HandleCategoryChange = (value) => {
    const next =
      typeof value === 'string'
        ? value
        : value?.target?.value ?? value?.value ?? '';
    SetFilters((prev) => ({ ...prev, categoryCd: next || '' }));
  };

  const HandleStatusFilterChange = (value) => {
    const next =
      typeof value === 'string'
        ? value
        : value?.target?.value ?? value?.value ?? '';
    SetFilters((prev) => ({ ...prev, statusCd: next || '' }));
  };

  const HandleCoChange = async (e) => {
    await handleCoChange(e);
    await SearchLogic1(Filters.searchText);
  };

  const HandleCustChange = async (e) => {
    await handleCustChange(e);
    await SearchLogic1(Filters.searchText);
  };

  useEffect(() => {
    if (!IsSearchReady) return;
    SearchLogic1(Filters.searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    IsSearchReady,
    searchInfo?.coCd,
    searchInfo?.custCd,
    Filters.categoryCd,
    Filters.statusCd,
  ]);

  return (
    <div className="wrap">
      <HEL_BASIC
        userInfo={userInfo}
        searchInfo={searchInfo}
        GridRef1={GridRef1}
        RowData1={RowData1}
        RowData2={RowData2}
        SelectedRowData1={SelectedRowData1}
        SetSelectedRowData1={SetSelectedRowData1}
        ChangedRowData1={ChangedRowData1}
        SetChangedRowData1={SetChangedRowData1}
        Form={Form}
        SetForm={SetForm}
        Filters={Filters}
        ReplyText={ReplyText}
        SetReplyText={SetReplyText}
        HandleCoChange={HandleCoChange}
        HandleCustChange={HandleCustChange}
        HandleCategoryChange={HandleCategoryChange}
        HandleStatusFilterChange={HandleStatusFilterChange}
        HandleRowClick1={HandleRowClick1}
        AddLogic1={AddLogic1}
        SearchLogic1={SearchLogic1}
        RefreshLogic1={RefreshLogic1}
        SaveLogic1={SaveLogic1}
        SaveStatusLogic1={SaveStatusLogic1}
        SaveReplyLogic1={SaveReplyLogic1}
        DeleteLogic1={DeleteLogic1}
        DeleteSelectedLogic1={DeleteSelectedLogic1}
        DeleteConfirmOpen={DeleteConfirmOpen}
        DeleteConfirmMessage={DeleteConfirmMessage}
        CloseDeleteConfirm={CloseDeleteConfirm}
        ConfirmDeleteLogic1={ConfirmDeleteLogic1}
        AttachFiles={AttachFiles}
        IsWritePage={IsWritePage}
        IsEditMode={IsEditMode}
        IsDetailOwner={IsDetailOwner}
        HandleFileDrop={HandleFileDrop}
        HandleFileSelect={HandleFileSelect}
        RemoveAttachLogic1={RemoveAttachLogic1}
        DownloadAttachLogic1={DownloadAttachLogic1}
        PreviewAttachLogic1={PreviewAttachLogic1}
        AttachPreviewOpen={AttachPreviewOpen}
        AttachPreviewItem={AttachPreviewItem}
        CloseAttachPreviewLogic1={CloseAttachPreviewLogic1}
        StatsOpen={StatsOpen}
        CategoryStats={CategoryStats}
        OpenStatsLogic1={OpenStatsLogic1}
        CloseStatsLogic1={CloseStatsLogic1}
        CloseWritePage={CloseWritePage}
        EditLogic1={EditLogic1}
        CancelEditLogic1={CancelEditLogic1}
      />
    </div>
  );
}

export default HEL_BASICF;
